import { FC } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import { AppPath } from '@/shared/constants/path';

import useHandleShipToCustomer from '../../components/Tile/hooks/handlers/useHandleShipToCustomer';

type ShipToCustomerDialogProps = {
  shipToCustomerState: ReturnType<typeof useHandleShipToCustomer>;
  loading?: boolean;
  onClose: () => void;
  proceed: (value: {
    shipping_profile_id: string;
    shipping_profile_zone_id: string;
    shipping_price?: string;
  }) => void;
};

const ShipToCustomerDialog: FC<ShipToCustomerDialogProps> = ({
  shipToCustomerState,
  loading,
  onClose,
  proceed,
}) => {
  const {
    shippingProfiles,
    selectedShippingProfileId,
    selectedZoneId,
    shippingPrice,
    setShippingPrice,
    onSelectShippingProfile,
    onSelectShippingProfileZone,
  } = shipToCustomerState;

  const selectedProfileItem = shipToCustomerState.shippingProfiles?.find(
    (item) => item.shipping_profile_id === selectedShippingProfileId,
  );

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: 'Ship To Customer',
        content: (
          <Stack sx={{ py: 1, gap: 2 }}>
            <FormControl>
              <InputLabel id='select-helper-label'>Shipping Profile</InputLabel>
              <Select
                labelId='select-helper-label'
                id='select-helper'
                value={selectedShippingProfileId}
                label='Fulfilment Provider'
                onChange={(e) => onSelectShippingProfile(e.target.value)}
              >
                {shippingProfiles?.map(
                  ({ display_name, shipping_profile_id }) => (
                    <MenuItem
                      key={shipping_profile_id}
                      value={shipping_profile_id}
                    >
                      <Stack spacing={0.5} direction='row' alignItems='center'>
                        <Typography variant='body1'>
                          {display_name || shipping_profile_id}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel id='select-helper-label1'>Shipping Zone</InputLabel>
              <Select
                labelId='select-helper-label1'
                id='select-helper'
                value={selectedZoneId}
                label='Fulfilment Provider'
                onChange={(e) => onSelectShippingProfileZone(e.target.value)}
                disabled={selectedProfileItem?.zones.length === 0}
              >
                {selectedProfileItem?.zones.map(
                  ({
                    delivery_country,
                    delivery_city,
                    shipping_profile_zone_id,
                    delivery_state,
                  }) => (
                    <MenuItem
                      key={shipping_profile_zone_id}
                      value={shipping_profile_zone_id}
                    >
                      <Stack spacing={0.5} direction='row' alignItems='center'>
                        <Typography variant='body1'>
                          {delivery_country}
                          {delivery_state ? `: ${delivery_state}` : ''}
                          {delivery_city ? `: ${delivery_city}` : ''}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ),
                )}
              </Select>
              {selectedProfileItem?.zones.length === 0 && (
                <FormHelperText>
                  Please complete shipping profile setup{' '}
                  <Link
                    component='a'
                    target='_blank'
                    href={`${AppPath.Shipping_Profiles}/${selectedProfileItem?.shipping_profile_id}`}
                  >
                    {selectedProfileItem?.shipping_profile_id.split('-')[0]}
                  </Link>
                </FormHelperText>
              )}
            </FormControl>

            <TextField
              label='Shipping Price'
              placeholder='Provide Shipping Price ...'
              value={shippingPrice}
              inputProps={{ type: 'number' }}
              onChange={(e) => setShippingPrice(e.target.value)}
            />
          </Stack>
        ),
        actions: [
          {
            variant: 'text',
            label: 'Cancel',
            onClick: onClose,
          },
          {
            variant: 'contained',
            label: 'Proceed',
            disabled: selectedProfileItem?.zones.length === 0,
            icon: loading ? (
              <CircularProgress
                size='small'
                sx={{ color: '#fff', width: 16, height: 16, mr: 1 }}
              />
            ) : null,
            onClick: () => {
              return proceed({
                shipping_profile_id: selectedShippingProfileId,
                shipping_profile_zone_id: selectedZoneId,
                shipping_price: shippingPrice || undefined,
              });
            },
          },
        ],
      }}
    />
  );
};

export default ShipToCustomerDialog;
